import type { UserFormSchema } from "@/components/non_registrant/user-form";
import { toast } from "@/components/ui/sonner";
import { UserType } from "@/constants";
import {
  createAdminUserMutation,
  getAdminUserOptions,
  getJurisdictionsOptions,
  updateAdminUserMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { client } from "@/hera-api/client/client.gen";
import type { CreateAdminUserData } from "@/hera-api/client/types.gen";
import { invalidateAdminUserQueries } from "@/lib/admin-user-query-invalidation";
import { getHeraApiBaseUrl } from "@/lib/hera-api-base-url";
import {
  canUserViewJurisdiction,
  getQueryErrorMessage,
  getUserType,
} from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import { useAuthQuerySetup } from "./use-auth-query-setup";

/** Dev-only: log the effective HTTP request for POST /shared/users (token redacted). */
function logCreateAdminUserRequestDebug(body: Record<string, unknown>) {
  const envBase = getHeraApiBaseUrl();
  const baseFromClient = client.getConfig().baseUrl ?? envBase;
  const base = String(baseFromClient).replace(/\/$/, "");
  const url = `${base}/shared/users`;
  const auth = client.getConfig().auth;
  const token = typeof auth === "string" ? auth : undefined;
  const extraHeaders = client.getConfig().headers;
  const cfg = client.getConfig();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: token
      ? `Bearer <redacted; ${token.length} chars>`
      : "<no token on API client — auth setup may not be complete>",
  };
  const serializedPreview = JSON.stringify(body);
  const payload = {
    method: "POST" as const,
    url,
    headers,
    extraClientHeaders: extraHeaders ?? {},
    bodyAsPlainObject: body,
    bodyJsonPretty: JSON.stringify(body, null, 2),
    howTheClientSendsIt: {
      note: "Hey API client-fetch merges jsonBodySerializer into config: your object is JSON.stringify’d into a single string before fetch(). It is not FormData, not a custom streaming encoder.",
      defaultContentType: "application/json (from client default headers)",
      bodySerializerOnConfig: typeof cfg.bodySerializer,
      serializedStringLength: serializedPreview.length,
      serializedStringExactlyWhatFetchWillSendAsEntityBody: serializedPreview,
    },
  };
  console.groupCollapsed(
    "[Hera] createAdminUser → POST /shared/users (app + client)",
  );
  console.log(payload);
  console.log("mutation options passed to createAdminUserMutation", { body });
  console.log(
    "DevTools may show Request.body as ReadableStream — that is normal for fetch(); open the collapsed “wire-level POST” group for the exact string on the wire.",
  );
  console.groupEnd();
}

export function useUserForm(userId: string | null) {
  const queryClient = useQueryClient();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { user } = useAuth0();
  const userType = getUserType(user);

  const isOperator = userType === UserType.OPERATOR;
  const isGovernment = userType === UserType.GOVERNMENT;

  // Get user - separate queries for each user type
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: errorUser,
  } = useQuery({
    ...getAdminUserOptions({
      path: {
        user_id: userId as string,
      },
      query: {
        include_deleted: true,
      },
    }),
    enabled: isAuthSetupComplete && !!userId,
  });

  const {
    data: jurisdictionsData,
    isLoading: isLoadingJurisdictions,
    error: errorJurisdictions,
  } = useQuery({
    ...getJurisdictionsOptions({}),
    enabled: isAuthSetupComplete && isOperator,
  });

  const createUserMutationFn = useMutation({
    ...createAdminUserMutation(),
  });

  const updateUserMutationFn = useMutation({
    ...updateAdminUserMutation(),
  });

  const filteredJurisdictions = jurisdictionsData?.jurisdictions.filter(
    (jurisdiction) => canUserViewJurisdiction(jurisdiction.id, user),
  );

  const handleSubmit = async (data: z.infer<typeof UserFormSchema>) => {
    if (!userId) {
      // Creating a new user
      try {
        if (data.user_type !== "government") {
          if (isGovernment) {
            toast({
              title: "Error",
              description: "Government users can only create government users",
              variant: "destructive",
            });
            return;
          }

          const body = {
            user_type: data.user_type,
            email: data.email,
            name: data.name,
            is_admin: data.is_admin,
            is_sales: data.is_sales,
          };
          logCreateAdminUserRequestDebug(body);
          await createUserMutationFn.mutateAsync({
            body,
          });
        } else {
          // For government users, the backend will automatically use their jurisdiction
          const requestBody: CreateAdminUserData["body"] = {
            user_type: "government",
            email: data.email,
            name: data.name,
            is_main_contact: data.is_main_contact,
            is_remittance_contact: data.is_remittance_contact,
            ...(data.jurisdiction_id
              ? { jurisdiction_id: data.jurisdiction_id }
              : {}),
          };

          logCreateAdminUserRequestDebug(
            requestBody as unknown as Record<string, unknown>,
          );
          await createUserMutationFn.mutateAsync({
            body: requestBody,
          });
        }

        toast({
          title: "Success",
          description: "User created successfully!",
          variant: "secondary",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `Failed to create user: ${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
        throw error;
      }
    } else {
      try {
        // Updating an existing user
        await updateUserMutationFn.mutateAsync({
          path: { user_id: userId },
          body: {
            email: data.email,
            name: data.name,
            is_deleted: data.is_deleted,
            ...(data.user_type === "operator"
              ? {
                  is_admin: data.is_admin,
                  is_sales: data.is_sales,
                }
              : {
                  is_main_contact: data.is_main_contact,
                  is_remittance_contact: data.is_remittance_contact,
                }),
          },
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
        throw error;
      }
    }

    // Await so the users list has the change before the sheet closes.
    await invalidateAdminUserQueries(queryClient);
  };

  return {
    // Data
    userData: userData
      ? {
          auth0_subject: userData.auth0_subject,
          email: userData.email,
          name: userData.name,
          user_type: userData.user_type,
          jurisdiction_id: userData.jurisdiction?.id ?? null,
          is_deleted: userData.is_deleted,
          is_main_contact: userData.is_main_contact,
          is_remittance_contact: userData.is_remittance_contact,
          is_admin: userData.is_admin,
          is_sales: userData.is_sales,
        }
      : null,

    jurisdictionsData:
      filteredJurisdictions?.map((jurisdiction) => ({
        id: jurisdiction.id,
        name: jurisdiction.name,
      })) ?? [],

    // Loading states
    isLoading: isLoadingUser || isLoadingJurisdictions,

    // Errors
    error: errorUser || errorJurisdictions,

    // Actions
    handleSubmit,
  };
}
